import { Copy } from "lucide-react";

import { ApiError, IN_USE_BY_OTHERS } from "../api/client";
import { Dialog, DialogBody } from "../components/Dialog";
import { Button } from "../components/ui";
import { t } from "../i18n/i18n";
import { plural } from "../plural";

/** Refus d'une écriture par ricochet (docs/17 §3.13, §8.3, lot 5) : un ordre de bataille ou un terrain sert à des
 *  dossiers que le compte ne peut pas modifier ; le modifier ou le supprimer changerait ces dossiers. Les dossiers
 *  lisibles sont nommés, les autres comptés ; « Dupliquer » est l'action attendue (la copie appartient au compte et se
 *  modifie librement). */

export interface InUse {
  exercises: { id: string; name: string }[];
  hidden: number;
}

/** Refus 409 `IN_USE_BY_OTHERS` d'une requête ; null pour tout autre échec. */
export function inUseOf(error: unknown): InUse | null {
  if (!(error instanceof ApiError) || error.code !== IN_USE_BY_OTHERS) return null;
  const extra = (error.detail ?? {}) as { exercises?: unknown; hidden?: unknown };
  const exercises = Array.isArray(extra.exercises)
    ? extra.exercises.filter((e): e is { id: string; name: string } => typeof e === "object" && e !== null && "name" in e)
    : [];
  return { exercises, hidden: typeof extra.hidden === "number" ? extra.hidden : 0 };
}

/** Texte du refus : « Cet élément sert à des dossiers que vous ne pouvez pas modifier : « A », « B » et 2 dossiers
 *  d'autres comptes. Le modifier ou le supprimer changerait ces dossiers : dupliquez-le pour l'adapter. » */
export function inUseText(inUse: InUse, kind: "odb" | "zone" = "odb"): string {
  const names = inUse.exercises.map((e) => `« ${e.name} »`).join(", ");
  const others = inUse.hidden ? t("et {n} dossiers d'autres comptes", { n: inUse.hidden }) : "";
  const list = names && others ? `${names} ${others}` : names || plural(inUse.hidden, t("dossier d'un autre compte"), t("dossiers d'autres comptes"));
  if (kind === "zone")
    // Un terrain ne se duplique pas : l'action attendue est d'en préparer un nouveau.
    return t("Ce terrain sert à des dossiers que vous ne pouvez pas modifier : {liste}. Le modifier ou le supprimer changerait ces dossiers : préparez un nouveau terrain pour l'adapter.", {
      liste: list,
    });
  return t("Cet élément sert à des dossiers que vous ne pouvez pas modifier : {liste}. Le modifier ou le supprimer changerait ces dossiers : dupliquez-le pour l'adapter.", {
    liste: list,
  });
}

export function InUseDialog({
  inUse,
  onDuplicate,
  onClose,
  busy = false,
  kind = "odb",
}: {
  inUse: InUse;
  /** Terrain : texte propre (rien à dupliquer). */
  kind?: "odb" | "zone";
  /** Absent : rien à dupliquer ici (terrain : préparer un nouveau terrain). */
  onDuplicate?: () => void;
  onClose: () => void;
  busy?: boolean;
}) {
  return (
    <Dialog
      title={t("Élément employé par d'autres dossiers")}
      onClose={onClose}
      width={480}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("Fermer")}
          </Button>
          {onDuplicate && (
            <Button variant="primary" onClick={onDuplicate} disabled={busy}>
              <Copy size={14} /> {t("Dupliquer")}
            </Button>
          )}
        </>
      }
    >
      <DialogBody>
        <p className="text-ui leading-snug text-ink">{inUseText(inUse, kind)}</p>
      </DialogBody>
    </Dialog>
  );
}
